import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ticket, CheckCircle, AlertCircle, ArrowRight, Gift, Loader2 } from 'lucide-react';
import './RedeemPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RedeemPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get('code') || '';
  
  const [couponCode, setCouponCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('hyperdriveToken');
    setIsLoggedIn(!!token);
  }, []);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    // Prevent double-clicks
    if (loading) return;
    
    setLoading(true);
    setError('');
    setCouponData(null);
    setProducts([]);

    // Use AbortController for clean request handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

    try {
      const url = `${API_URL}/api/coupons/redeem/${encodeURIComponent(couponCode.trim().toUpperCase())}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal,
        cache: 'no-store', // Prevent browser caching
      });
      
      clearTimeout(timeoutId);
      
      // Read the response body only once
      const responseText = await response.text();
      
      // Parse the JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('JSON parse error:', parseErr, 'Response:', responseText);
        throw new Error('Server returned an invalid response. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid coupon code');
      }

      setCouponData(data.coupon);
      setProducts(data.products || []);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.message || 'Invalid or expired coupon code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToCheckout = (productId, productType) => {
    if (isLoggedIn) {
      // User is logged in - go directly to purchase/checkout page
      navigate(`/purchase/${productType}/${productId}?coupon=${couponCode}`);
    } else {
      // User needs to login first - pass coupon info
      navigate(`/login?coupon=${couponCode}&product=${productId}&type=${productType}`);
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage' || coupon.discount_type === 'percent') {
      return `${coupon.discount_value}% OFF`;
    }
    return `$${coupon.discount_value} OFF`;
  };

  const calculateDiscountedPrice = (price, coupon) => {
    if (coupon.discount_type === 'percentage' || coupon.discount_type === 'percent') {
      return price - (price * coupon.discount_value / 100);
    }
    return Math.max(0, price - coupon.discount_value);
  };

  return (
    <div className="redeem-page">
      <div className="redeem-container">
        <div className="redeem-header">
          <div className="redeem-icon">
            <Gift size={48} />
          </div>
          <h1>Redeem Your Coupon</h1>
          <p>Enter your exclusive coupon code to unlock special offers</p>
        </div>

        <div className="redeem-form">
          <div className="coupon-input-group">
            <Ticket size={20} className="input-icon" />
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="coupon-input"
              onKeyPress={(e) => e.key === 'Enter' && validateCoupon()}
            />
            <button 
              onClick={validateCoupon} 
              className="validate-btn"
              disabled={loading}
            >
              {loading ? <Loader2 size={20} className="spin" /> : 'Apply'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}
        </div>

        {couponData && (
          <div className="coupon-result">
            <div className="coupon-success">
              <CheckCircle size={24} />
              <div>
                <h3>Coupon Applied!</h3>
                <p className="discount-badge">{formatDiscount(couponData)}</p>
              </div>
            </div>

            {products.length > 0 ? (
              <div className="available-products">
                <h3>Available Products</h3>
                <div className="products-grid">
                  {products.map(product => (
                    <div key={product.id} className="product-card">
                      <div className="product-info">
                        <h4>{product.name}</h4>
                        <p className="product-description">{product.description}</p>
                        <div className="product-pricing">
                          <span className="original-price">${product.price}</span>
                          <span className="discounted-price">
                            ${calculateDiscountedPrice(product.price, couponData).toFixed(2)}
                          </span>
                        </div>
                        {product.features && product.features.length > 0 && (
                          <ul className="product-features">
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <li key={idx}>
                                <CheckCircle size={14} />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <button 
                        className="checkout-btn"
                        onClick={() => handleProceedToCheckout(product.id, product.type)}
                      >
                        Get Started
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-products">
                <p>This coupon is valid but no specific products are assigned to it.</p>
                <button 
                  className="browse-btn"
                  onClick={() => navigate('/login?coupon=' + couponCode)}
                >
                  Browse All Products
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="redeem-footer">
          <p>Don't have a coupon? <a href="/">View our regular pricing</a></p>
        </div>
      </div>
    </div>
  );
};

export default RedeemPage;
